import { FormikProps, getIn, FormikValues } from 'formik';

import { RoutePath, SkillSetRoutes } from '@astrumu/route-mapping';

import { MATCH_MAPPING } from '../constants';

import {
  RangeInt,
  JobRecommendation,
  SearchLearningRecommendationsInput
} from '@/graphql/generated';
import { LearningRecommendationQueryFilters, Match } from '../types';

export const decimalNumber = (num: number) => Math.ceil(num * 100) / 100;

export const getFieldError = <T extends FormikValues>(
  name: Extract<keyof T, string>,
  errors?: FormikProps<T>['errors'],
  touched?: FormikProps<T>['touched']
): string =>
  getIn(errors, name) && getIn(touched, name) ? getIn(errors, name) : '';

export const firstCharToUppercase = (name: string) =>
  name.charAt(0).toUpperCase() + name.slice(1);

export const replaceCamelCaseWithSpaces = (title: string) =>
  title.replace(/([A-Z])/g, ' $1');

export const getAuth0RedirectUri = (
  allowedAuth0CallbackPaths: RoutePath<SkillSetRoutes>[]
) => {
  const { origin, pathname } = window.location;

  if (
    allowedAuth0CallbackPaths.includes(pathname as RoutePath<SkillSetRoutes>)
  ) {
    return `${origin}${pathname}`;
  }

  return origin;
};

export const formatDate = (date: Date, type: 'short' | 'medium' | 'long') => {
  switch (type) {
    case 'long':
      return new Date(date).toLocaleDateString('en-us', {
        year: 'numeric',
        month: 'long',
        day: '2-digit'
      });

    case 'short':
      return new Date(date).toLocaleDateString('en-us');

    default:
    case 'medium':
      return new Date(date).toLocaleDateString('en-us', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
  }
};

export const getMatchByScore = (
  score: JobRecommendation['score']
): { label: string; match: Match } => {
  const defaultMatch = Match.Fair;
  const matchByScore = (Object.keys(MATCH_MAPPING) as Array<Match>)
    .reverse()
    .find(match => score * 100 >= MATCH_MAPPING[match].minScore);

  const match = matchByScore || defaultMatch;

  return {
    match,
    label: MATCH_MAPPING[match].label
  };
};

export const boolToText = (value?: boolean | null) => (value ? 'Yes' : 'No');

export const getRangeInput = (rangeFilterValue?: string[]) => {
  if (rangeFilterValue?.length) {
    return rangeFilterValue.map(value => {
      const [from, to] = value.split('-');

      return { from: Number(from) || 0, to: Number(to) || null };
    });
  }

  return [];
};

export const prepareLearningRecommendationFilters = ({
  credentialTypeIds,
  benefitIds,
  deliveryMethodIds,
  locationIds,
  jobRoleIds,
  price,
  duration,
  name
}: LearningRecommendationQueryFilters): SearchLearningRecommendationsInput => ({
  name,
  credentialTypeIds,
  benefitIds,
  deliveryMethodIds,
  locationIds,
  jobRoleIds,
  duration: getRangeInput(duration),
  price: getRangeInput(price)
});

export function getRange(
  from: RangeInt['from'],
  to: RangeInt['to'],
  formatFn?: (rangeItem: RangeInt['from']) => void
) {
  const formattedFrom = formatFn ? formatFn(from) : from;
  const formattedTo = formatFn ? formatFn(to) : to;

  if (from === to) {
    return formattedFrom;
  }

  if (from && to) {
    return `${formattedFrom} - ${formattedTo}`;
  }

  if (to) {
    return formattedTo;
  }

  if (from) {
    return formattedFrom;
  }

  return 0;
}

export function formatUrl(url: string) {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }

  return url;
}
